import React from 'react';
import { Link } from 'react-router-dom';
import { useFetch } from '../hooks/useFetch';
import { getIconComponent } from '../utils/iconMapper';
import PageLoader from '../components/PageLoader';
import {
    ArrowRight,
    Smartphone,
    Award,
    Sparkles,
    CheckCircle,
    Send,
    Zap,
    Users,
    MessageCircle,
    TrendingUp,
} from 'lucide-react';

function Features() {
    // Fetch features from API
    const { data: features, loading, error } = useFetch('/features');

    // Loading state with PageLoader
    if (loading) {
        return <PageLoader message="Loading features..." />;
    }

    // Error state
    if (error) {
        return (
            <div
                className="error-state"
                style={{
                    minHeight: '60vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                }}
            >
                <p style={{ color: 'var(--danger)', fontSize: '18px' }}>
                    Failed to load features.
                </p>
                <p style={{ color: 'var(--muted)' }}>{error}</p>
            </div>
        );
    }

    // Use fetched features or empty array
    const featureList = features || [];

    return (
        <>
            {/* Hero Section - Navy Background */}
            <div className="page-hero">
                <div className="eyebrow light" style={{ display: 'inline-flex' }}>
                    Church member care software
                </div>
                <h1>
                    Everything you need to{' '}
                    <span className="highlight">care for your congregation.</span>
                </h1>
                <p>
                    Attendance tracking, follow-up automation, and SMS communication —
                    all designed for how African churches actually work.
                </p>
            </div>

            {/* Features Grid - White Background */}
            <section>
                <div className="wrap">
                    <div
                        className="section-head center animate-fadeInUp"
                        style={{ margin: '0 auto 56px', maxWidth: 640 }}
                    >
                        <div className="eyebrow">Features overview</div>
                        <h2>
                            One platform. <span className="highlight">Every member cared for.</span>
                        </h2>
                        <p>
                            From the moment someone walks through your doors to the follow-up
                            that keeps them connected — FaithConnect handles it all.
                        </p>
                    </div>

                    <div className="features-grid">
                        {featureList.length > 0 ? (
                            featureList.map((feature, index) => {
                                const Icon = getIconComponent(feature.icon) || CheckCircle;
                                return (
                                    <div
                                        key={feature.id || index}
                                        className="feature-card animate-fadeInUp"
                                        style={{ animationDelay: `${(index + 1) * 100}ms` }}
                                    >
                                        <div
                                            className="feature-icon"
                                            style={{
                                                background: feature.color?.bg || 'var(--cream)',
                                                color: feature.color?.text || 'var(--ink)',
                                            }}
                                        >
                                            <Icon size={28} />
                                        </div>
                                        <h3>{feature.title}</h3>
                                        <p>{feature.description}</p>
                                    </div>
                                );
                            })
                        ) : (
                            // Fallback static features if API returns empty
                            <>
                                <div className="feature-card animate-fadeInUp delay-100">
                                    <div
                                        className="feature-icon"
                                        style={{ background: '#FEF3E2', color: '#E8752A' }}
                                    >
                                        <CheckCircle size={28} />
                                    </div>
                                    <h3>Attendance tracking</h3>
                                    <p>
                                        Mark present in seconds from any phone — no smartphone or
                                        app required for check-in staff.
                                    </p>
                                </div>
                                <div className="feature-card animate-fadeInUp delay-200">
                                    <div
                                        className="feature-icon"
                                        style={{ background: '#E5F5EC', color: '#2D8F5E' }}
                                    >
                                        <Send size={28} />
                                    </div>
                                    <h3>SMS software</h3>
                                    <p>
                                        Reach your whole congregation with bulk SMS members
                                        actually read — no data needed on their end.
                                    </p>
                                </div>
                                <div className="feature-card animate-fadeInUp delay-300">
                                    <div
                                        className="feature-icon"
                                        style={{ background: '#E6EEFF', color: '#3B7DD8' }}
                                    >
                                        <Zap size={28} />
                                    </div>
                                    <h3>Automated follow-up</h3>
                                    <p>
                                        Birthday messages, visitor welcome drips, and absence
                                        alerts send themselves — your team just responds.
                                    </p>
                                </div>
                                <div className="feature-card animate-fadeInUp delay-400">
                                    <div
                                        className="feature-icon"
                                        style={{ background: '#F0EAFF', color: '#7C3AED' }}
                                    >
                                        <Users size={28} />
                                    </div>
                                    <h3>Groups & ministries</h3>
                                    <p>
                                        Track youth, choir, women's ministry, and prayer teams
                                        separately, with their own engagement metrics.
                                    </p>
                                </div>
                                <div className="feature-card animate-fadeInUp delay-500">
                                    <div
                                        className="feature-icon"
                                        style={{ background: '#FEF3E2', color: '#D4AF37' }}
                                    >
                                        <MessageCircle size={28} />
                                    </div>
                                    <h3>Prayer request tracking</h3>
                                    <p>
                                        Members submit requests by SMS; your pastoral team sees
                                        them in one place, never lost in a paper box.
                                    </p>
                                </div>
                                <div className="feature-card animate-fadeInUp delay-600">
                                    <div
                                        className="feature-icon"
                                        style={{ background: '#E5F5EC', color: '#0A1628' }}
                                    >
                                        <TrendingUp size={28} />
                                    </div>
                                    <h3>Care reports</h3>
                                    <p>
                                        A monthly member care summary for your board — who was
                                        reached, who wasn't, and what changed.
                                    </p>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="center" style={{ marginTop: 48 }}>
                        <Link to="/features" className="btn-ghost">
                            See all features in detail <ArrowRight size={18} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Designed for African Churches - Cream Background (static) */}
            <section className="section-light">
                <div className="wrap">
                    <div
                        className="section-head center animate-fadeInUp"
                        style={{ margin: '0 auto 56px', maxWidth: 560 }}
                    >
                        <div className="eyebrow">Designed for African churches</div>
                        <h2>
                            Built around <span className="highlight">how you actually work.</span>
                        </h2>
                    </div>
                    <div className="pillars-grid">
                        <div className="pillar-card animate-fadeInUp delay-200">
                            <Smartphone className="pillar-icon" size={40} />
                            <div className="pillar-label">SMS-first</div>
                            <h3>No app required</h3>
                            <p>
                                Every feature works over SMS. Your members don't need
                                smartphones, data plans, or WhatsApp — they just need their
                                phones.
                            </p>
                        </div>
                        <div className="pillar-card animate-fadeInUp delay-400">
                            <Award className="pillar-icon" size={40} />
                            <div className="pillar-label">Localized</div>
                            <h3>Built for Ghana</h3>
                            <p>
                                Mobile money billing, local support hours, and a team that
                                understands the rhythms of Ghanaian church life.
                            </p>
                        </div>
                        <div className="pillar-card animate-fadeInUp delay-600">
                            <Sparkles className="pillar-icon" size={40} />
                            <div className="pillar-label">Simple</div>
                            <h3>Easy to use</h3>
                            <p>
                                No training required. Anyone on your team can take attendance,
                                send messages, and run reports within minutes.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA - Navy Gradient (static) */}
            <section className="final-cta">
                <div className="eyebrow light" style={{ position: 'relative', zIndex: 2 }}>
                    Ready to get started?
                </div>
                <h2>
                    Start caring for your <span className="highlight">members today.</span>
                </h2>
                <p>
                    Join 60+ churches across Ghana using FaithConnect to track, follow up,
                    and communicate with their congregations.
                </p>
                <Link to="/signup" className="btn-primary">
                    Start free trial <ArrowRight size={18} />
                </Link>
            </section>
        </>
    );
}

export default Features;